import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { api, TweetSyncResponse, AccountInfo, ResumeSyncDto, TweetSyncStatus } from '../../api/af-social-tracker-api';
import { useDataStore } from '../data/dataStore';

export interface ProcessedTweetSync {
  id: string;
  tokenData: any; // PulseCard에 전달할 토큰 데이터
  communityHandles: Array<{ handle: string; role: 'ADMIN' | 'MOD' }>;
  associatedHandles?: string[];
  communityId?: string | null;
  creditsUsed: number;
  totalTweets: number;
  syncStatus: TweetSyncStatus;
  lastExecutedAt: string;
}

interface TweetSyncStoreState {
  // state
  syncs: TweetSyncResponse[];
  isInitialized: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  isStoppingAll: boolean;
  error: string | null;
  lastSyncsUpdate: string | null;
  currentPage: number;
  hasMore: boolean;
  pageLimit: number;
  searchQuery: string; // 검색 쿼리 (심볼 또는 컨트랙트 주소)
  
  // account info state
  accountInfo: AccountInfo | null;
  isAccountInfoLoading: boolean;
  accountInfoError: string | null;

  // actions
  fetchSyncs: () => Promise<void>;
  loadMoreSyncs: () => Promise<void>;
  searchSyncs: (query: string) => Promise<void>;
  resumeSync: (id: string, dto?: ResumeSyncDto) => Promise<void>;
  stopSync: (id: string) => Promise<void>;
  stopAllSyncs: () => Promise<{ message: string; stoppedCount: number }>;
  handleStopAll: () => Promise<void>;
  getProcessedSyncs: () => ProcessedTweetSync[];
  setSearchQuery: (query: string) => void;
  clearSearchQuery: () => Promise<void>;
  clearError: () => void;
  
  // account info actions
  fetchAccountInfo: () => Promise<void>;
  clearAccountInfoError: () => void;

}

export const useTweetSyncStore = create<TweetSyncStoreState>()(
  devtools(
    (set, get) => ({
      // 기본 상태
      syncs: [],
      isInitialized: false,
      isLoading: false,
      isLoadingMore: false,
      isStoppingAll: false,
      error: null,
      lastSyncsUpdate: null,
      currentPage: 1,
      hasMore: true,
      pageLimit: 50,
      searchQuery: '',
      
      // account info state
      accountInfo: null,
      isAccountInfoLoading: false,
      accountInfoError: null,

      // 트윗 싱크 목록 조회 (초기 로드)
      fetchSyncs: async () => {
        set({ isLoading: true, error: null, currentPage: 1 });
        try {
          const limit = get().pageLimit;
          const result = await api.getTweetSyncs({ page: 1, limit });
          const newSyncs = result.data || [];
          // limit만큼 받으면 다음 페이지가 있다고 추정
          const hasMore = newSyncs.length === limit;
          
          set({ 
            syncs: newSyncs, 
            isInitialized: true,
            isLoading: false,
            hasMore,
            currentPage: 1,
            lastSyncsUpdate: new Date().toISOString()
          });
        } catch (error) {
          console.error('트윗 싱크 목록 조회 실패:', error);
          set({ 
            error: '트윗 싱크 목록 조회 실패', 
            isLoading: false,
            hasMore: false
          });
        }
      },

      // 더 많은 트윗 싱크 로드 (무한 스크롤)
      loadMoreSyncs: async () => {
        const { currentPage, hasMore, isLoadingMore, isLoading, pageLimit, searchQuery } = get();
        
        // 검색 중이면 무한 스크롤 비활성화
        if (searchQuery.trim()) {
          return;
        }
        
        // 이미 로딩 중이거나 더 이상 로드할 데이터가 없으면 중단
        if (isLoadingMore || isLoading || !hasMore) {
          return;
        }

        set({ isLoadingMore: true, error: null });
        try {
          const nextPage = currentPage + 1;
          const result = await api.getTweetSyncs({ page: nextPage, limit: pageLimit });
          const newSyncs = result.data || [];
          
          // limit만큼 받으면 다음 페이지가 있다고 추정
          const hasMoreNext = newSyncs.length === pageLimit;
          
          set({ 
            syncs: [...get().syncs, ...newSyncs],
            currentPage: nextPage,
            hasMore: hasMoreNext,
            isLoadingMore: false,
            lastSyncsUpdate: new Date().toISOString()
          });
        } catch (error) {
          console.error('트윗 싱크 추가 로드 실패:', error);
          set({ 
            error: '트윗 싱크 추가 로드 실패', 
            isLoadingMore: false,
            hasMore: false
          });
        }
      },

      // 검색 API 호출
      searchSyncs: async (query: string) => {
        set({ isLoading: true, error: null, searchQuery: query });
        try {
          const result = await api.searchTweetSyncs(query);
          const searchResults = result.data || [];
          
          set({ 
            syncs: searchResults,
            isInitialized: true,
            isLoading: false,
            hasMore: false, // 검색 결과는 페이지네이션 없음
            currentPage: 1,
            lastSyncsUpdate: new Date().toISOString()
          });
        } catch (error) {
          console.error('트윗 싱크 검색 실패:', error);
          set({ 
            error: '트윗 싱크 검색 실패', 
            isLoading: false,
            syncs: [],
            hasMore: false
          });
        }
      },

      // 개별 싱크 재개
      resumeSync: async (id: string, dto?: ResumeSyncDto) => {
        try {
          await api.resumeTweetSync(id, dto);
          // 목록 새로고침 (첫 페이지부터 다시 로드)
          await get().fetchSyncs();
        } catch (error) {
          console.error('트윗 싱크 재개 실패:', error);
          set({ error: '트윗 싱크 재개 실패' });
        }
      },

      // 개별 싱크 중지
      stopSync: async (id: string) => {
        try {
          await api.stopTweetSync(id);
          // 목록 새로고침 (첫 페이지부터 다시 로드)
          await get().fetchSyncs();
        } catch (error) {
          console.error('트윗 싱크 중지 실패:', error);
          set({ error: '트윗 싱크 중지 실패' });
        }
      },

      // 전체 싱크 중지 (API 호출만)
      stopAllSyncs: async () => {
        try {
          const result = await api.stopAllTweetSyncs();
          // 목록 새로고침
          await get().fetchSyncs();
          return result;
        } catch (error) {
          console.error('전체 트윗 싱크 중지 실패:', error);
          set({ error: '전체 트윗 싱크 중지 실패' });
          throw error;
        }
      },

      // 전체 중지 핸들러 (확인 모달 + 로딩 상태 포함)
      handleStopAll: async () => {
        const { isStoppingAll } = get();
        if (isStoppingAll) return;
        
        const confirmed = window.confirm('모든 트윗 싱크를 중지하시겠습니까?');
        if (!confirmed) return;
        
        try {
          set({ isStoppingAll: true });
          const res = await get().stopAllSyncs();
          alert(`전체 중지 완료: ${res?.stoppedCount || 0}개 중지됨`);
        } catch (e) {
          alert('전체 중지 중 오류가 발생했습니다. 콘솔을 확인하세요.');
          console.error(e);
        } finally {
          set({ isStoppingAll: false });
        }
      },

      // 가공된 싱크 데이터 반환
      getProcessedSyncs: () => {
        const { syncs } = get();
        const getTokenData = useDataStore.getState().getTokenData;
        
        return syncs.map((s) => {
          const tokenData = getTokenData(s.tokenAddress);
          return {
            id: s._id,
            tokenData: tokenData!,
            tokenAddress: s.tokenAddress,
            tokenSymbol: s.tokenSymbol,
            communityHandles: s.communityHandles || [],
            associatedHandles: s.associatedHandles || [],
            communityId: s.communityId || null,
            creditsUsed: s.creditUsed ?? 0,
            creditUsedInUsd: 0.00001 * (s.creditUsed ?? 0),
            totalTweets: s.totalTweetsCollected ?? 0,
            syncStatus: s.status,
            lastExecutedAt: s.lastExecutedAt || new Date().toISOString(),
          };
        }).filter((i) => !!i.tokenData);
      },

      // 검색 쿼리 설정
      setSearchQuery: (query: string) => {
        set({ searchQuery: query });
      },

      // 검색 쿼리 클리어 (전체 목록 다시 로드)
      clearSearchQuery: async () => {
        set({ searchQuery: '' });
        // 검색 초기화 시 전체 목록 다시 로드
        await get().fetchSyncs();
      },

      // 에러 클리어
      clearError: () => set({ error: null }),

      // 계정 정보 조회
      fetchAccountInfo: async () => {
        set({ isAccountInfoLoading: true, accountInfoError: null });
        try {
          const result = await api.getMyAccountInfo();
          set({ 
            accountInfo: result.data || null,
            isAccountInfoLoading: false
          });
        } catch (error) {
          console.error('계정 정보 조회 실패:', error);
          set({ 
            accountInfoError: '계정 정보 조회 실패', 
            isAccountInfoLoading: false 
          });
        }
      },

      // 계정 정보 에러 클리어
      clearAccountInfoError: () => set({ accountInfoError: null }),
    }),
    {
      name: 'TweetSyncStore',
      enabled: process.env.NODE_ENV === 'development'
    }
  )
);
